import { type FC, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { type Options } from '@popperjs/core/lib/types'
import { Button, Dropdown, DropdownContent, layoutStore } from 'shared/ui'
import { type Conversation } from 'entities/Conversation/model/Conversation'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { ConversationAssignDropdownContent } from 'pages/conversations/ui/ConversationAssignDropdown'
import { useConversationListContext } from 'pages/conversations/ui/ConversationList/context'
import { ButtonClose } from 'pages/conversations/ui/ConversationCard/ui/ConversationDropdown/actions/Close'
import { ButtonMarkAsUnread } from 'pages/conversations/ui/ConversationCard/ui/ConversationDropdown/actions/MarkAsUnread'
import { ButtonMakePriority } from 'pages/conversations/ui/ConversationCard/ui/ConversationDropdown/actions/MakePriority'
import { ButtonOptInStatus } from 'pages/conversations/ui/ConversationCard/ui/ConversationDropdown/actions/OptInStatus'
import { ButtonMarkAsPending } from 'pages/conversations/ui/ConversationCard/ui/ConversationDropdown/actions/MarkAsPending'
import { ButtonTransfer } from 'pages/conversations/ui/ConversationCard/ui/ConversationDropdown/actions/Transfer'

type IConversationDropdownProps = {
  conversation: Conversation
  onChangeOpen?: (isOpen: boolean) => void
}

export const ConversationDropdown: FC<IConversationDropdownProps> = observer(
  ({ conversation, onChangeOpen }) => {
    const { userPermissionsStore } = useEntitiesContext()
    const store = useConversationListContext()
    const [showAssignDropdown, setShowAssignDropdown] = useState(false)

    const onCloseAssignDropdown = () => setShowAssignDropdown(false)

    return (
      <Dropdown
        ariaLabel={'ConversationDropdown'}
        width={224}
        icon={'moreHorizontal'}
        iconButtonProps={{
          variant: 'contained',
          color: 'tertiary2',
          transparentInactive: true,
          ariaLabel: 'ConversationDropdown_trigger',
        }}
        placement={'bottom-start'}
        withToggleTriggerOpen
        onChangeOpen={onChangeOpen}
        customComponent={(onCloseMenu, dropdownId) => (
          <DropdownContent>
            <ButtonClose
              conversation={conversation}
              clickCallback={onCloseMenu}
              onMouseEnter={onCloseAssignDropdown}
            />
            <ButtonMarkAsUnread
              conversation={conversation}
              clickCallback={onCloseMenu}
              onMouseEnter={onCloseAssignDropdown}
            />
            {!conversation.isGroup && (
              <Dropdown
                ariaLabel={'ConversationDropdownGroup'}
                show={showAssignDropdown}
                onChangeOpen={setShowAssignDropdown}
                parentsDropdownId={[dropdownId]}
                width={layoutStore.isXsMobileView ? 204 : 192}
                placement={layoutStore.isXsMobileView ? 'top' : 'right-start'}
                PopperProps={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: ({ placement }: { placement: Options['placement'] }) => {
                          if (placement === 'top' || placement === 'bottom') {
                            return [-10, 0]
                          }
                          if (placement === 'right-start') {
                            return [-4, -4]
                          }
                          if (placement === 'right-end') {
                            return [4, -4]
                          }
                          return [-4, -4]
                        },
                      },
                    },
                  ],
                }}
                triggerComponent={() => (
                  <Button
                    fontWeight={400}
                    typeBtn='menuItem'
                    icon='userLine'
                    text='Assign'
                    iconRightProps={{
                      icon: 'chevronRight',
                    }}
                    fullWidth
                    onMouseEnter={() => setShowAssignDropdown(true)}
                  />
                )}
                customComponent={(onCloseMenuChild) => (
                  <ConversationAssignDropdownContent
                    store={store.conversationAssignDropdownStore}
                    conversationId={conversation.id}
                    show={showAssignDropdown}
                    onCloseMenu={() => {
                      onCloseMenuChild()
                      onCloseMenu()
                    }}
                  />
                )}
              />
            )}
            <ButtonMakePriority
              conversation={conversation}
              clickCallback={onCloseMenu}
              onMouseEnter={onCloseAssignDropdown}
            />
            <ButtonOptInStatus
              contactsIds={[conversation.contact_id]}
              clickCallback={onCloseMenu}
              onMouseEnter={onCloseAssignDropdown}
            />
            <ButtonMarkAsPending
              conversation={conversation}
              clickCallback={onCloseMenu}
              onMouseEnter={onCloseAssignDropdown}
            />
            {userPermissionsStore.getItem('can_transfer_conversation') && (
              <ButtonTransfer
                conversationIds={[conversation.id]}
                clickCallback={onCloseMenu}
                onMouseEnter={onCloseAssignDropdown}
              />
            )}
          </DropdownContent>
        )}
      />
    )
  }
)
